import { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { DotBricksLoading, ListPagination, Modal } from 'src/common/components';
import { Toast } from 'src/common/components';
import { Table } from 'src/common/components/Table';
import { BrandIdentityStatus } from 'src/tcr/constants';
import { BrandProps, LegalEntityProps } from 'src/tcr/types';

import { createTcrBrand, updateTcrBrand } from '../api';
import { useLegalEntityListContext } from '../context';

interface LegalEntityListProps {
  legalEntities: LegalEntityProps[];
  refetch: () => void;
}

const List = ({ legalEntities, refetch }: LegalEntityListProps): JSX.Element => {
  const { actions, state } = useLegalEntityListContext();
  const { loadingLegalEntities, currentPage, totalCount } = state;
  const [itemCreatingBrand, setItemCreatingBrand] = useState<string | null>(null);
  const { paginate } = actions;
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const triggerBrandCreation = async (legalEntity: LegalEntityProps): Promise<any> => {
    setItemCreatingBrand(legalEntity.id);
    createTcrBrand(legalEntity)
      .then((response) => {
        if (!response?.data?.error) {
          const brandId = response?.data?.brand_id;
          Toast.success(`Tcr Brand Created${brandId ? ': ' + brandId : ''}`);
        }
        refetch();
        return response;
      })
      .catch((error) => {
        if (error?.request && error?.request?.response) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          const parsed = JSON.parse(error?.request?.response);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          Toast.error(parsed?.message);
        } else {
          Toast.error('Error while creating tcr brand');
        }
      })
      .finally(() => {
        setItemCreatingBrand(null);
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const triggerBrandUpdateAndRevet = async (brand?: BrandProps): Promise<any> => {
    if (brand) {
      setItemCreatingBrand(brand.id);
      updateTcrBrand(brand)
        .then((response) => {
          if (!response?.data?.error) {
            const brandId = response?.data?.brand_id;
            Toast.success(`Brand update success for brand${brandId ? ': ' + brandId : ''}`);
          }
          refetch();
          return response;
        })
        .catch((error) => {
          if (error?.request && error?.request?.response) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            const parsed = JSON.parse(error?.request?.response);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            Toast.error(parsed?.message);
          } else {
            Toast.error('Error during update & revet');
          }
        })
        .finally(() => {
          setItemCreatingBrand(null);
        });
    }
  };

  const getTcrBrandModal = (item: LegalEntityProps): JSX.Element => {
    const hasBrands = item.brands && !!item.brands.length;
    const isUnverified = hasBrands && item.brands[0].brand_identity_status == 2;
    const setForRevet = hasBrands && item.brands[0].set_for_revet;
    const brand = hasBrands ? item.brands[0] : undefined;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleBrandCreation = async () => {
      await triggerBrandCreation(item);
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleBrandUpdateAndRevet = async () => {
      await triggerBrandUpdateAndRevet(brand);
    };
    return !hasBrands ? (
      <Modal
        toggleTarget={
          <button disabled={itemCreatingBrand !== null} className="btn btn-secondary">
            {itemCreatingBrand === item.id ? (
              <DotBricksLoading className="mx-12" />
            ) : (
              'Create Tcr Brand'
            )}
          </button>
        }
        actionFunc={handleBrandCreation}
        cancelText="Cancel"
        actionText="Continue"
      >
        <div>
          <h3>Creating a new TCR brand has an aditional cost</h3>
          <p className="mt-4">Do you want to continue?</p>
        </div>
      </Modal>
    ) : (
      <Modal
        toggleTarget={
          <button
            disabled={!isUnverified || setForRevet || itemCreatingBrand !== null}
            className="btn btn-secondary"
          >
            {itemCreatingBrand === item.id ? (
              <DotBricksLoading className="mx-12" />
            ) : (
              'Update and Revet'
            )}
          </button>
        }
        actionFunc={handleBrandUpdateAndRevet}
        cancelText="Cancel"
        actionText="Continue"
      >
        <div>
          <h3>Make sure to fully update the Legal Entity before Update & Revet</h3>
          <p className="mt-4">Do you want to continue?</p>
        </div>
      </Modal>
    );
  };

  const columns = useMemo(() => {
    return [
      { title: 'Name', index: 'name' },
      {
        title: 'Accounts',
        index: 'accounts',
        renderer: (item: LegalEntityProps) => {
          const firstAccountName = item.accounts?.[0]?.name;
          const accountsAmount = item.accounts?.length - 1;
          return (
            <div>
              {firstAccountName}
              {accountsAmount > 0 ? `+${accountsAmount}` : ''}
            </div>
          );
        },
      },
      {
        title: 'Brand ID / Status',
        index: 'brands',
        renderer: (item: LegalEntityProps) => {
          if (item.brands && item.brands.length) {
            const brandId = item.brands[0].brand_id;
            const brandStatusIndex = item.brands[0]?.brand_identity_status
              ? item.brands[0].brand_identity_status
              : 0;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const brandStatus = BrandIdentityStatus[brandStatusIndex];
            return (
              <div>
                <div>{`${brandId} / ${brandStatus}`}</div>
              </div>
            );
          }
        },
      },
      {
        title: 'Actions',
        index: 'actions',
        renderer: (item: LegalEntityProps) => {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          const handleLegalEntityEdit = () => {
            navigate(`${item.id}`);
          };

          return (
            <div className="flex">
              {getTcrBrandModal(item)}
              <button className="btn btn-secondary ml-4" onClick={handleLegalEntityEdit}>
                Edit
              </button>
            </div>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, itemCreatingBrand]);

  if (!legalEntities?.length)
    return (
      <div className="p-4 bg-gray-0.0 rounded-md">
        Not found legal entities to show. Add someone
      </div>
    );

  return (
    <>
      <div className="scrollbar-thin flex-1 overflow-auto">
        <Table<LegalEntityProps>
          data={legalEntities}
          columns={columns}
          loading={loadingLegalEntities}
        />
      </div>
      <ListPagination currentPage={currentPage} totalCount={totalCount} setCurrentPage={paginate} />
    </>
  );
};

export default List;
